import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Users, Fuel, AlertTriangle, TrendingUp, TrendingDown, Filter, RefreshCw, Wrench, DollarSign, MapPin, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { relatoriosService, DashboardData, AlertasData, AtividadeRecente, AtividadeTipo } from '../../services/relatorios';
import { formatCurrency, formatDate, formatKilometers } from '../../utils/formatters';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

/**
 * Valor curto para eixo e rotulo de coluna: R$ 34,3k.
 *
 * Num grafico com muitas colunas o valor por extenso nao cabe entre uma e
 * outra. O numero exato continua no tooltip, que e onde alguem vai olhar
 * quando precisar dele.
 */
const valorCompacto = (v: number): string =>
  v >= 1000
    ? `R$ ${(v / 1000).toFixed(1).replace('.', ',')}k`
    : `R$ ${v.toFixed(0)}`;

// Estilo (cor/icone/rota) distinto por modulo, usado no feed de atividades.
const ATIVIDADE_CONFIG: Record<AtividadeTipo, { label: string; color: string; bg: string; icon: React.ElementType; rota: string }> = {
  abastecimento: { label: 'Abastecimento', color: '#d97706', bg: '#fef3c7', icon: Fuel, rota: 'abastecimentos' },
  manutencao: { label: 'Manutenção', color: '#7c3aed', bg: '#ede9fe', icon: Wrench, rota: 'manutencoes' },
  despesa: { label: 'Despesa', color: '#059669', bg: '#d1fae5', icon: DollarSign, rota: 'despesas' },
  viagem: { label: 'Viagem', color: '#0891b2', bg: '#cffafe', icon: MapPin, rota: 'viagens' },
};

const formatarDataHora = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState<string>('mes');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [alertasData, setAlertasData] = useState<AlertasData | null>(null);
  const [atividades, setAtividades] = useState<AtividadeRecente[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, alertasRes, atividadesRes] = await Promise.all([
        relatoriosService.getDashboard(periodo),
        relatoriosService.getAlertas(),
        relatoriosService.getAtividadesRecentes({ per_page: 8 }),
      ]);
      if (dashRes.success) setDashboardData(dashRes.data);
      if (alertasRes.success) setAlertasData(alertasRes.data);
      if (atividadesRes.success) setAtividades(atividadesRes.data);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // O card "Alertas pendentes" mostra o total real, e nao o numero de itens
  // carregados: as listas de /alertas vem truncadas num limite fixo.
  // O total vem do proprio servidor, que ja publica `resumo.total_geral` como
  // numero canonico de alertas.
  //
  // Antes o card somava a mao cinco das listas, enquanto a tela de Alertas
  // somava todas. O gestor via um numero no painel, clicava, e encontrava
  // outro — dois numeros com o mesmo nome na mesma sessao. Pior: cada lista
  // nova exigia lembrar de editar este arquivo, e foi assim que a divergencia
  // nasceu.
  //
  // O recuo abaixo cobre `resumo` ausente (backend mais antigo que o front) e
  // soma o tamanho de TODOS os arrays presentes, sem listar chave por chave,
  // para nao repetir o mesmo erro quando surgir a proxima lista.
  const totalAlertasPendentes = alertasData?.resumo
    ? alertasData.resumo.total_geral
    : Object.values(alertasData ?? {})
        .filter((categoria): categoria is Record<string, any[]> =>
          typeof categoria === 'object' && categoria !== null && !Array.isArray(categoria))
        .flatMap((categoria) => Object.values(categoria))
        .filter(Array.isArray)
        .reduce((soma, lista) => soma + lista.length, 0);

  const totalDespesasVencidas = alertasData?.resumo?.listas['despesas-vencidas']?.total
    ?? (alertasData?.despesas?.vencidas?.length || 0);

  // Mock data - substituir por dados reais da API
  const stats = [
    {
      title: 'Total de veículos',
      value: loading ? '...' : String(dashboardData?.veiculos?.total || 0),
      change: `${dashboardData?.veiculos?.ativos || 0} ativos`,
      changeType: 'positive' as const,
      icon: Car,
      color: 'primary'
    },
    {
      title: 'Motoristas ativos',
      value: loading ? '...' : String(dashboardData?.motoristas?.ativos || 0),
      change: `${dashboardData?.motoristas?.cnh_vencendo || 0} CNH vencendo`,
      changeType: (dashboardData?.motoristas?.cnh_vencendo || 0) > 0 ? 'negative' as const : 'positive' as const,
      icon: Users,
      color: 'success'
    },
    {
      title: 'Gasto mensal',
      value: loading ? '...' : formatCurrency(dashboardData?.custos?.total || 0),
      change: `Combustível: ${formatCurrency(dashboardData?.custos?.combustivel || 0)}`,
      changeType: 'negative' as const,
      icon: Fuel,
      color: 'warning'
    },
    {
      title: 'Alertas pendentes',
      value: loading ? '...' : String(totalAlertasPendentes),
      change: `${totalDespesasVencidas} despesas vencidas`,
      changeType: 'negative' as const,
      icon: AlertTriangle,
      color: 'danger',
      // O único card que leva a algum lugar hoje: o número aqui é uma
      // pendência, e quem olha quer ver quais são. Os outros três são
      // informativos e não têm uma tela equivalente para abrir.
      destino: '/alertas',
    }
  ];

  const recentAlerts = alertasData ? [
    ...(alertasData.motoristas?.cnh_vencida || []).map((m: any) => ({
      id: `cnh-v-${m.id}`,
      type: 'danger',
      message: `CNH do motorista ${m.nome} esta vencida`,
      time: '',
      link: `/motoristas/${m.id}`,
    })),
    ...(alertasData.motoristas?.cnh_vencendo || []).map((m: any) => ({
      id: `cnh-p-${m.id}`,
      type: 'warning',
      message: `CNH do motorista ${m.nome} vence em breve`,
      time: '',
      link: `/motoristas/${m.id}`,
    })),
    ...(alertasData.veiculos?.licenciamento_vencendo || []).map((v: any) => ({
      id: `lic-${v.id}`,
      type: 'danger',
      message: `Licenciamento do veiculo ${v.placa} vence em breve`,
      time: '',
      link: `/veiculos/${v.id}`,
    })),
    ...(alertasData.veiculos?.seguro_vencendo || []).map((v: any) => ({
      id: `seg-${v.id}`,
      type: 'warning',
      message: `Seguro do veiculo ${v.placa} vence em breve`,
      time: '',
      link: `/veiculos/${v.id}`,
    })),
    ...(alertasData.despesas?.vencidas || []).map((d: any) => ({
      id: `desp-${d.id}`,
      type: 'danger',
      message: `Despesa vencida: ${d.descricao}`,
      time: '',
      link: `/despesas/${d.id}`,
    })),
    ...(alertasData.manutencoes?.preventivas_pendentes || []).map((m: any) => ({
      id: `man-${m.id}`,
      type: 'info',
      message: `Manutenção preventiva agendada: ${m.descricao}`,
      time: '',
      link: `/manutencoes/${m.id}`,
    })),
  ].slice(0, 6) : [
    {
      id: 1,
      type: 'warning',
      message: 'Carregando alertas...',
      time: '',
      link: undefined as string | undefined,
    },
  ];

  const getStatColorClasses = (color: string) => {
    const colors = {
      primary: 'bg-primary-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      danger: 'bg-danger-500'
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  const getAlertTypeClasses = (type: string) => {
    const types = {
      warning: 'bg-warning-100 text-warning-800 border-warning-200',
      danger: 'bg-danger-100 text-danger-800 border-danger-200',
      info: 'bg-primary-100 text-primary-800 border-primary-200'
    };
    return types[type as keyof typeof types] || types.info;
  };

  // Dados para graficos
  const custosData = dashboardData ? [
    { name: 'Combustível', valor: Number(dashboardData.custos?.combustivel) || 0 },
    { name: 'Manutenção', valor: Number(dashboardData.custos?.manutencao) || 0 },
    { name: 'Despesas', valor: Number(dashboardData.custos?.despesas) || 0 },
  ] : [];

  // O periodo escolhido nao tem movimento, mas a empresa tem.
  //
  // Sem este aviso a tela mostra tudo zerado e nao diz por que — e quem olha
  // conclui que o sistema perdeu os dados. Acontece com cliente real todo dia
  // 1o, porque o filtro padrao e "mes".
  const semMovimentoNoPeriodo =
    !loading
    && !!dashboardData?.ultimo_lancamento
    && Number(dashboardData?.custos?.total ?? 0) === 0
    && (dashboardData?.viagens?.total ?? 0) === 0;

  const dataDoUltimo = dashboardData?.ultimo_lancamento
    ? formatDate(dashboardData.ultimo_lancamento)
    : null;

  // O servidor ja devolve ordenado e sem categoria zerada; a tela so exibe.
  const despesasPorCategoria = dashboardData?.despesas?.por_categoria ?? [];

  const veiculosStatusData = dashboardData ? [
    { name: 'Ativos', value: dashboardData.veiculos?.ativos || 0 },
    { name: 'Inativos', value: dashboardData.veiculos?.inativos || 0 },
    { name: 'Manutenção', value: dashboardData.veiculos?.manutencao || 0 },
  ].filter(d => d.value > 0) : [];

  const manutencoesTipoData = dashboardData ? [
    { name: 'Preventivas', value: dashboardData.manutencoes?.preventivas || 0 },
    { name: 'Corretivas', value: dashboardData.manutencoes?.corretivas || 0 },
  ].filter(d => d.value > 0) : [];

  const despesasStatusData = dashboardData ? [
    { name: 'Pagas', value: dashboardData.despesas?.pagas || 0 },
    { name: 'Pendentes', value: dashboardData.despesas?.pendentes || 0 },
  ].filter(d => d.value > 0) : [];

  const viagensData = dashboardData ? [
    { name: 'Concluidas', value: dashboardData.viagens?.concluidas || 0 },
    { name: 'Em andamento', value: dashboardData.viagens?.em_andamento || 0 },
  ].filter(d => d.value > 0) : [];

  const periodoLabels: Record<string, string> = {
    dia: 'Hoje',
    semana: 'Esta Semana',
    mes: 'Este mês',
    ano: 'Este Ano',
  };

  // Rotulo com o valor DENTRO da fatia (evita corte nas bordas do card;
  // o nome de cada fatia fica na legenda abaixo do grafico).
  const renderPieValue = ({ cx, cy, midAngle, innerRadius, outerRadius, value }: any) => {
    if (!value) return null;
    const RAD = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight={700}>
        {value}
      </text>
    );
  };

  const CustomTooltipCurrency = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
          <p className="text-secondary-600 mt-1">
            Visão geral do sistema de gestão de veículos
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtro de Periodo */}
          <div className="flex items-center gap-1 bg-white border border-secondary-200 rounded-lg p-1">
            <Filter className="h-4 w-4 text-secondary-400 ml-2" />
            {['dia', 'semana', 'mes', 'ano'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriodo(p)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  periodo === p
                    ? 'bg-primary-500 text-white'
                    : 'text-secondary-600 hover:bg-secondary-100'
                }`}
              >
                {p === 'dia' ? 'Dia' : p === 'semana' ? 'Semana' : p === 'mes' ? 'Mês' : 'Ano'}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            className="p-2 text-secondary-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
            title="Atualizar dados"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="text-sm text-secondary-500">
            {periodoLabels[periodo]}
          </div>
        </div>
      </div>

      {semMovimentoNoPeriodo && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
          <span>
            Nenhum lançamento em <b>{periodoLabels[periodo].toLowerCase()}</b>.
            {dataDoUltimo && <> O mais recente é de <b>{dataDoUltimo}</b>.</>}
          </span>
          {periodo !== 'ano' && (
            <button
              type="button"
              onClick={() => setPeriodo('ano')}
              className="font-semibold underline underline-offset-2 hover:text-amber-700"
            >
              Ver o ano inteiro
            </button>
          )}
        </div>
      )}

      {/* Acoes Rapidas (compactas, no topo) */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => navigate('/veiculos/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <Car className="h-4 w-4 text-primary-600" /> Novo veículo
        </button>
        <button onClick={() => navigate('/motoristas/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <Users className="h-4 w-4 text-success-600" /> Novo motorista
        </button>
        <button onClick={() => navigate('/abastecimentos/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <Fuel className="h-4 w-4 text-warning-600" /> Abastecimento
        </button>
        <button onClick={() => navigate('/manutencoes/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <Wrench className="h-4 w-4 text-danger-600" /> Manutenção
        </button>
        <button onClick={() => navigate('/despesas/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <DollarSign className="h-4 w-4 text-emerald-600" /> Despesa
        </button>
        <button onClick={() => navigate('/viagens/novo')} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
          <MapPin className="h-4 w-4 text-cyan-600" /> Viagem
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;

          // <button> e nao <div onClick>: assim o card entra na navegacao por
          // teclado e o leitor de tela anuncia que da para clicar. `text-left`
          // desfaz a centralizacao que o botao traz por padrao.
          const Envolucro = stat.destino ? 'button' : 'div';
          const propsDoClique = stat.destino
            ? {
                type: 'button' as const,
                onClick: () => navigate(stat.destino!),
                title: 'Ver os alertas',
                className:
                  'card p-6 w-full text-left transition-colors hover:border-primary-300 hover:bg-primary-50/40 '
                  + 'focus:outline-none focus:ring-2 focus:ring-primary-500',
              }
            : { className: 'card p-6' };

          return (
            <Envolucro key={index} {...propsDoClique}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-secondary-600 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-secondary-900">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    {stat.changeType === 'positive' ? (
                      <TrendingUp className="h-4 w-4 text-success-500 mr-1" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-danger-500 mr-1" />
                    )}
                    <span className={`text-sm font-medium ${
                      stat.changeType === 'positive' ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-lg ${getStatColorClasses(stat.color)} flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </Envolucro>
          );
        })}
      </div>

      {/* ===== GRAFICOS ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grafico de Custos por Categoria */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Custos por categoria</h2>
          <p className="text-sm text-secondary-500 mb-4">{periodoLabels[periodo]}</p>
          {custosData.some(d => d.valor > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={custosData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                <Tooltip content={<CustomTooltipCurrency />} />
                <Bar dataKey="valor" name="Valor" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                  {custosData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                  <LabelList
                    dataKey="valor"
                    position="top"
                    formatter={(v: any) => formatCurrency(Number(v))}
                    style={{ fontSize: 12, fill: '#374151', fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-secondary-400">
              <p>Sem dados de custos para o período</p>
            </div>
          )}
        </div>

        {/* Grafico Pizza - Status dos Veiculos */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Status da frota</h2>
          <p className="text-sm text-secondary-500 mb-4">{dashboardData?.veiculos?.total || 0} veículos no total</p>
          {veiculosStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={veiculosStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={88}
                  paddingAngle={4}
                  dataKey="value"
                  isAnimationActive={false}
                  label={({ name, value, percent }: { name: string; value?: number; percent?: number }) => `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`}
                >
                  {veiculosStatusData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => [value, 'Veiculos']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-secondary-400">
              <p>Sem dados de veículos</p>
            </div>
          )}
        </div>
      </div>

      {/* Graficos - Linha 2: despesas por tipo, ocupando a largura toda */}
      <div className="grid grid-cols-1 gap-6">
        {/* Despesas por tipo
            Sozinho na propria linha, e nao ao lado de outro card: sao ate doze
            colunas, e espremido em meia largura os nomes se sobrepoem.
            Fica depois de "Custos por categoria" porque e o detalhamento dele —
            aquele mostra que a empresa gastou X em despesas, este mostra do que
            esse X e feito. */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Despesas por tipo</h2>
          <p className="text-sm text-secondary-500 mb-4">{periodoLabels[periodo]}</p>
          {despesasPorCategoria.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={despesasPorCategoria} margin={{ top: 24, right: 8, left: 8, bottom: 46 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                {/* interval={0} para nenhum rotulo sumir: com dez ou mais
                    categorias o Recharts esconde alternados, e a coluna fica
                    sem nome. O angulo e a margem inferior existem para o nome
                    caber inteiro — a 0 grau eles se sobrepoem. */}
                <XAxis
                  dataKey="rotulo"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={valorCompacto} />
                <Tooltip content={<CustomTooltipCurrency />} />
                <Bar dataKey="valor" name="Valor" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                  {despesasPorCategoria.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                  {/* Valor abreviado, e nao por extenso: com dez colunas
                      "R$ 22.954,79" encosta em "R$ 22.848,69" e os dois viram
                      um borrao. O valor exato fica no tooltip. */}
                  <LabelList
                    dataKey="valor"
                    position="top"
                    formatter={(v: any) => valorCompacto(Number(v))}
                    style={{ fontSize: 11, fill: '#374151', fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[320px] text-secondary-400">
              <p>Nenhuma despesa no período</p>
            </div>
          )}
        </div>

      </div>

      {/* Graficos - Linha 3: os tres em pizza, lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Tipos de manutenção */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Manutenções</h2>
          <p className="text-sm text-secondary-500 mb-4">{dashboardData?.manutencoes?.total || 0} no período</p>
          {manutencoesTipoData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={manutencoesTipoData} cx="50%" cy="50%" outerRadius={75} paddingAngle={4} dataKey="value" isAnimationActive={false}
                  labelLine={false} label={renderPieValue}
                >
                  <Cell fill="#8b5cf6" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-secondary-400">
              <p>Sem manutenções</p>
            </div>
          )}
        </div>

        {/* Status das Despesas */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Despesas</h2>
          <p className="text-sm text-secondary-500 mb-4">{dashboardData?.despesas?.total || 0} no período</p>
          {despesasStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={despesasStatusData} cx="50%" cy="50%" outerRadius={75} paddingAngle={4} dataKey="value" isAnimationActive={false}
                  labelLine={false} label={renderPieValue}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-secondary-400">
              <p>Sem despesas</p>
            </div>
          )}
        </div>

        {/* Viagens */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Viagens</h2>
          <p className="text-sm text-secondary-500 mb-4">{formatKilometers(dashboardData?.viagens?.km_total || 0)} rodados</p>
          {viagensData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={viagensData} cx="50%" cy="50%" outerRadius={75} paddingAngle={4} dataKey="value" isAnimationActive={false}
                  labelLine={false} label={renderPieValue}
                >
                  <Cell fill="#06b6d4" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-secondary-400">
              <p>Sem viagens</p>
            </div>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">Alertas recentes</h2>
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              Ver todos
            </button>
          </div>

          <div className="space-y-3">
            {recentAlerts.map((alert) => {
              const clickable = Boolean(alert.link);
              return (
                <div
                  key={alert.id}
                  onClick={() => alert.link && navigate(alert.link)}
                  className={`p-3 rounded-lg border ${getAlertTypeClasses(alert.type)} ${clickable ? 'cursor-pointer hover:brightness-95 transition' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{alert.message}</p>
                    {clickable && <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-70" />}
                  </div>
                  {alert.time && <p className="text-xs mt-1 opacity-75">{alert.time}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activities - feed individual, por modulo, clicavel */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">Atividades recentes</h2>
          </div>

          <div className="space-y-2">
            {atividades.length === 0 ? (
              <p className="text-sm text-secondary-400 py-4">Nenhuma atividade recente.</p>
            ) : (
              atividades.map((atv) => {
                const cfg = ATIVIDADE_CONFIG[atv.tipo];
                const Icon = cfg.icon;
                return (
                  <div
                    key={`${atv.tipo}-${atv.id}`}
                    onClick={() => navigate(`/${cfg.rota}/${atv.id}`)}
                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
                      <Icon className="h-5 w-5" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                          {cfg.label}
                        </span>
                        <p className="text-sm font-medium text-secondary-900 truncate">{atv.titulo}</p>
                      </div>
                      <p className="text-xs text-secondary-600 truncate">{atv.descricao}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-secondary-400">{formatarDataHora(atv.data)}</span>
                      <ChevronRight className="h-4 w-4 text-secondary-300" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
